import { useEffect } from 'react';
import { ArrowUpRight, Mail } from 'lucide-react';

const FONT_LINK_ID = 'macrify-mail-coming-soon-fonts';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';

const SERIF_STACK = "'DM Serif Display', Georgia, serif";
const SANS_STACK = "'Plus Jakarta Sans', system-ui, sans-serif";

const REASSURANCES = [
  { id: 'matters', label: 'Your matters' },
  { id: 'deadlines', label: 'Your deadlines' },
  { id: 'drafts', label: 'Your drafts' },
];

export default function ComingSoon() {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;

    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);

  return (
    <main
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden px-6 py-16 sm:px-10"
      style={{ backgroundColor: '#0B1A2E', fontFamily: SANS_STACK }}
    >
      {/* Local keyframes — kept inline so this page has zero external dependencies. */}
      <style>{`
        @keyframes macrifyMailDriftA {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(4%, -5%, 0) scale(1.08); }
        }
        @keyframes macrifyMailDriftB {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(-5%, 4%, 0) scale(1.05); }
        }
        @keyframes macrifyMailRise {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Hidden defs so the mark's icon can carry a true brand gradient stroke. */}
      <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="macrifyMailIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F7FAFE" />
            <stop offset="100%" stopColor="#B8DFFA" />
          </linearGradient>
        </defs>
      </svg>

      {/* Background: layered navy → cyan gradient field with slow drift. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 20% 15%, rgba(74, 173, 224, 0.22), transparent 55%), radial-gradient(circle at 82% 78%, rgba(41, 121, 201, 0.28), transparent 50%), linear-gradient(160deg, #0B1A2E 0%, #0B1A2E 45%, #132D52 78%, #1D4E8F 130%)',
          }}
        />
        <div
          className="motion-safe:animate-[macrifyMailDriftA_24s_ease-in-out_infinite] absolute -top-1/4 left-[8%] h-[42rem] w-[42rem] rounded-full opacity-40 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(74,173,224,0.35), transparent 70%)' }}
        />
        <div
          className="motion-safe:animate-[macrifyMailDriftB_28s_ease-in-out_infinite] absolute -bottom-1/3 right-[6%] h-[38rem] w-[38rem] rounded-full opacity-30 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(29,78,143,0.45), transparent 70%)' }}
        />
        {/* Subtle grain for texture, avoids a flat gradient. */}
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      {/* Foreground card */}
      <div
        className="motion-safe:animate-[macrifyMailRise_0.9s_ease-out_forwards] motion-reduce:opacity-100 relative z-10 w-full max-w-2xl rounded-3xl border border-white/10 bg-white/[0.04] px-7 py-12 opacity-0 shadow-2xl backdrop-blur-xl sm:px-14 sm:py-16"
        style={{ boxShadow: '0 40px 120px -40px rgba(11, 26, 46, 0.9)' }}
      >
        {/* Mark + wordmark */}
        <div className="mb-10 flex items-center justify-center gap-3.5 sm:justify-start">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #1D4E8F 0%, #2979C9 55%, #4AADE0 100%)',
              boxShadow: '0 12px 30px -8px rgba(41, 121, 201, 0.65)',
            }}
          >
            <Mail
              className="h-6 w-6"
              strokeWidth={1.75}
              stroke="url(#macrifyMailIconGradient)"
              aria-hidden="true"
            />
          </div>
          <span
            className="text-xl font-normal tracking-tight text-[#F7FAFE] sm:text-2xl"
            style={{ fontFamily: SERIF_STACK }}
          >
            Macrify <span className="text-[#7EC8F0]">Mail</span>
          </span>
        </div>

        <div className="text-center sm:text-left">
          <span className="inline-block rounded-full border border-[#4AADE0]/30 bg-[#4AADE0]/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#7EC8F0]">
            Coming soon
          </span>

          <h1
            className="mt-6 text-4xl leading-[1.05] font-normal text-[#F7FAFE] sm:text-6xl"
            style={{ fontFamily: SERIF_STACK }}
          >
            Macrify Mail
          </h1>

          <p
            className="mt-4 text-xl leading-snug text-[#B8DFFA] sm:text-2xl"
            style={{ fontFamily: SERIF_STACK }}
          >
            The email client built for how law firms actually work.
          </p>

          <p className="mt-6 max-w-lg text-base leading-relaxed text-[#B8DFFA]/80 sm:text-lg">
            A fast, focused inbox for your firm — with your deadlines, your matters, and your
            drafts one click away. Arriving soon.
          </p>

          <ul className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:justify-start">
            {REASSURANCES.map((item, index) => (
              <li
                key={item.id}
                className="flex items-center gap-2 text-sm font-medium text-[#7EC8F0]"
              >
                {index > 0 && (
                  <span aria-hidden="true" className="h-1 w-1 rounded-full bg-[#4AADE0]/60" />
                )}
                {item.label}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-start">
            <a
              href="https://macrify.me/connect"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-[#0B1A2E] shadow-lg transition-transform duration-200 ease-out hover:scale-[1.03] focus-visible:ring-2 focus-visible:ring-[#4AADE0] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1A2E] focus-visible:outline-none motion-reduce:transition-none"
              style={{
                background: 'linear-gradient(120deg, #B8DFFA 0%, #7EC8F0 55%, #4AADE0 100%)',
                boxShadow: '0 16px 36px -12px rgba(74, 173, 224, 0.55)',
              }}
            >
              Get early access
              <ArrowUpRight
                className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transition-none"
                aria-hidden="true"
              />
            </a>

            <a
              href="https://macrify.me"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-[#B8DFFA]/70 underline decoration-[#4AADE0]/40 underline-offset-4 transition-colors duration-200 hover:text-[#F7FAFE] focus-visible:ring-2 focus-visible:ring-[#4AADE0] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1A2E] focus-visible:outline-none"
            >
              Visit macrify.me
            </a>
          </div>
        </div>
      </div>

      <footer className="absolute inset-x-0 bottom-6 z-10 text-center">
        <p className="text-xs font-medium tracking-wide text-[#B8DFFA]/40">
          Built by Macrify &middot; &copy; {new Date().getFullYear()} Macrify LLC
        </p>
      </footer>
    </main>
  );
}
